import type { DatasetUploadListScope, UploadStatusGroup } from "@/api";
import { uploadStatusGroups } from "./statusFilterGroups";

/**
 * 업로드 현황 탭의 조회 축. URL 이 소유한다(rules.md §17). `?tab=uploads`·`?page=` 와 함께
 * 공유된다.
 * - 범위(내/전체)는 admin 전용 스위처다. 그래서 `all` 일 때만 키를 남긴다.
 * - 상태는 "전체" 가 아니라 그룹 값일 때만 키를 남긴다.
 * admin 이 아닌데 `scope=all` 이 들어오면 패널이 권한을 확인해 `me` 로 바꾼다(이 모듈은 형식만
 * 확인한다).
 */
export type DatasetUploadFilterState = {
  scope: DatasetUploadListScope;
  statusGroup: UploadStatusGroup;
};

export const EMPTY_DATASET_UPLOAD_FILTERS: DatasetUploadFilterState = {
  scope: "me",
  statusGroup: "all",
};

const PARAM = { scope: "scope", status: "status" } as const;
const SCOPE_ALL = "all";

function parseStatusGroup(value: string | null): UploadStatusGroup {
  const match = uploadStatusGroups.find((group) => group.value === value);
  return match === undefined ? "all" : match.value;
}

export function readDatasetUploadFilters(params: URLSearchParams): DatasetUploadFilterState {
  return {
    scope: params.get(PARAM.scope) === SCOPE_ALL ? "all" : "me",
    statusGroup: parseStatusGroup(params.get(PARAM.status)),
  };
}

// 자기 키만 지우고 다시 쓴다. 입력은 변경하지 않는다.
export function writeDatasetUploadFilters(
  params: URLSearchParams,
  state: DatasetUploadFilterState,
): URLSearchParams {
  const next = new URLSearchParams(params);
  if (state.scope === "all") {
    next.set(PARAM.scope, SCOPE_ALL);
  } else {
    next.delete(PARAM.scope);
  }
  if (state.statusGroup === "all") {
    next.delete(PARAM.status);
  } else {
    next.set(PARAM.status, state.statusGroup);
  }
  return next;
}

export type DatasetUploadFilterChip = {
  axis: "statusGroup";
  label: string;
};

// 칩은 적용된 조건만 표시한다. 범위(내/전체)는 조건이 아니라 목록 선택이다. 그래서 칩을 만들지 않는다.
export function datasetUploadFilterChips(
  state: DatasetUploadFilterState,
): DatasetUploadFilterChip[] {
  if (state.statusGroup === "all") {
    return [];
  }
  const label = uploadStatusGroups.find((group) => group.value === state.statusGroup)?.label;
  return label === undefined ? [] : [{ axis: "statusGroup", label: `상태: ${label}` }];
}
